/**
 * Real marketplace routes — `listMarketplace` and `publishToMarketplace`.
 *
 * Server contract (see `crates/gi-api-server/src/routes/marketplace.rs`):
 *
 *   GET  /marketplace/listings?q=&domain=&limit=
 *        → { rows: ListingRow[], total: number }
 *
 *   POST /marketplace/publish
 *        body: SignedMarketplaceListing { body: ListingBody, signature: hex }
 *        → SignedPublishReceipt
 *
 * Shape-gap notes (none of these block the UI today, but worth
 * flagging for when the server grows richer fields):
 *
 *   - The server's `ListingRow` does NOT return `description`,
 *     `published_at`, `rating`, `review_count`, `installs`,
 *     `contract`, or `lineage`. The UI's `MarketplaceListing` type
 *     requires those; we fill with zeros / stubs + mark the row.
 *   - `domain` comes back as Rust's Debug form (`"Process"`). We
 *     lowercase-map it to the UI's `DomainType` tag.
 *   - Publish validation on the server is strict: `listing_id` must
 *     equal the canonical-signing-hash of `body - listing_id`,
 *     signed by the author pubkey of the referenced GI. The UI's
 *     per-browser "operator key" will not satisfy this unless the
 *     GI being published was Sparked via real mode AND we retrieve
 *     its author secret from the mirrored mock store. We do exactly
 *     that here. A publish attempt on a demo-seeded GI (for which
 *     the UI has no secret key) returns an explicit error before
 *     even hitting the server.
 */

import { ed25519 } from '@noble/curves/ed25519';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils';

import type {
  CertificationLevel,
  CreatorProfile,
  DomainType,
  EntityId,
  Lineage,
  MarketplaceFilters,
  MarketplaceListing,
  MarketplaceReview,
  ProductListing,
  PublishInput,
  SubscribeRequest,
  SubscribeResponse,
  TrustedTimestamp,
} from '../types';
import { ApiHttpError, apiFetch } from '../http';
import { canonicalHash, canonicalJsonStringify } from '../mock/sign';
import { getRecord } from '../mock/store';

interface ServerListingRow {
  listing_id: string;
  name: string;
  /** Phase 4.7 — projected from `body.description` (4 KiB cap). */
  description?: string;
  domain: string;
  gi_root_id: string;
  gi_version: string;
  author_pubkey: string;
  price: string;
  currency: string;
  certification_level: string;
  tags: string[];
  /** Phase 4.7 — projected from `body.created_at`, ISO 8601 UTC. */
  published_at?: string;
  /** Phase 4.7 — Class-B server-maintained sidecar (zero default). */
  rating?: number;
  review_count?: number;
  installs?: number;
}

interface ServerListingsResponse {
  rows: ServerListingRow[];
  total: number;
}

export async function listMarketplace(
  filters: MarketplaceFilters = {},
): Promise<MarketplaceListing[]> {
  const params = new URLSearchParams();
  if (filters.search) params.set('q', filters.search);
  if (filters.domain && typeof filters.domain === 'string') {
    params.set('domain', filters.domain);
  }
  const query = params.toString();
  const path = `/marketplace/listings${query ? `?${query}` : ''}`;
  const response = await apiFetch<ServerListingsResponse>(path);
  return response.rows.map(rowToListing);
}

/**
 * Resolve a marketplace listing by `id` against the real backend.
 *
 * The detail-page route (`/marketplace/:id`) accepts two id shapes:
 *
 *   - The canonical listing id (`lst_<24-hex>`) returned by a publish
 *     round-trip. Matched against `root_id` (which the list endpoint
 *     projects from `gi_root_id`) and against any tag.
 *   - A human-readable slug (e.g. `citizen-mirror`). Matched against
 *     the tags array — the seed pipeline puts the slug as the first
 *     tag on every engine-curated listing.
 *
 * Returns `null` when nothing matches. The server has no
 * `/marketplace/listings/:id` endpoint today; we resolve by fetching
 * the (small, cached) list and filtering client-side. That stays
 * cheap until the catalogue grows past a few hundred entries.
 */
export async function getMarketplaceListing(
  id: string,
): Promise<{ listing: MarketplaceListing; reviews: MarketplaceReview[] } | null> {
  if (!id) return null;
  const listings = await listMarketplace({});
  const match = listings.find((l) => listingMatchesId(l, id));
  if (!match) return null;
  // Reviews aren't projected by the list endpoint — return an empty
  // array. The Class-B sidecar that holds reviews lands in Phase 4.5;
  // until then, every freshly-seeded listing carries zero reviews.
  return { listing: match, reviews: [] };
}

function listingMatchesId(listing: MarketplaceListing, id: string): boolean {
  if (listing.root_id === id) return true;
  // Tag-based slug lookup. The seed pipeline puts the human-readable
  // slug as `tags[0]` on every engine-curated listing; tagline is
  // populated from `tags[0]` in `rowToListing`, so checking both
  // catches mock-mode and real-mode shapes.
  if (listing.tagline === id) return true;
  return false;
}

export async function publishToMarketplace(
  input: PublishInput,
): Promise<MarketplaceListing> {
  const record = getRecord(input.root_id);
  if (!record) {
    throw new ApiHttpError({
      status: 400,
      code: 'gi-not-local',
      title: 'GI not found in local Vault',
      detail:
        'Publish requires the GI to have been Sparked via this browser (its author secret key is needed to sign the listing). Spark the GI in real mode and try again.',
    });
  }
  const secretHex = record.secret_key_hex;
  if (!secretHex) {
    throw new ApiHttpError({
      status: 400,
      code: 'gi-missing-secret',
      title: 'No author secret for this GI',
      detail:
        'This GI was created without a local author key (e.g. via the demo seeder). Publishing to the real marketplace requires the author secret.',
    });
  }

  const now: TrustedTimestamp = nowTimestamp();
  const authorPubkeyHex = stripTaggedHex(record.manifest.author.public_key);
  const giHashHex = stripTaggedHex(record.manifest.wasm_hash);
  const priceFixed = twelveDigitFixedPoint(input.price_usd);

  // Body built in canonical field order. `listing_id` is derived
  // from the canonical-signing-hash of the remaining fields — see
  // `MarketplaceListing::canonical_signing_hash` on the server.
  const bodyWithoutId: Record<string, unknown> = {
    name: record.manifest.name,
    description: input.description || record.manifest.description,
    domain: domainToWire(record.manifest.domain),
    author_pubkey: authorPubkeyHex,
    author_root_id: record.manifest.root_id,
    gi_root_id: record.manifest.root_id,
    gi_version: record.manifest.version,
    gi_hash: giHashHex,
    price: priceFixed,
    currency: 'USD',
    certification_level: certificationToWire(input.certification),
    tags: input.tagline
      ? input.tagline.split(/\s*,\s*/).filter(Boolean).slice(0, 16)
      : [],
    created_at: now,
    updated_at: now,
  };
  const listingId = `lst_${canonicalHashHex(bodyWithoutId).slice(0, 24)}`;
  const body = { listing_id: listingId, ...bodyWithoutId };

  const secret = hexToBytes(secretHex);
  const signingBytes = new TextEncoder().encode(canonicalJsonStringify(body));
  const signatureHex = bytesToHex(ed25519.sign(signingBytes, secret));

  await apiFetch<unknown>('/marketplace/publish', {
    method: 'POST',
    body: { body, signature: signatureHex },
  });

  // Convert the body we just submitted into the UI shape — the
  // server's receipt only returns an id and a hash, not enough to
  // populate the card. Using the canonical body is sufficient.
  const submittedDescription =
    input.description || record.manifest.description;
  return {
    root_id: record.manifest.root_id,
    version: record.manifest.version,
    name: record.manifest.name,
    tagline: input.tagline || record.manifest.description,
    description: submittedDescription,
    domain: record.manifest.domain,
    author_name: record.manifest.author.name,
    author_public_key: record.manifest.author.public_key,
    published_at: now,
    price_usd: Math.max(0, Math.floor(input.price_usd)),
    rating: 0,
    review_count: 0,
    installs: 0,
    certification: input.certification,
    contract: record.contract,
    lineage: record.lineage,
  };
}

// ─── Wire helpers ────────────────────────────────────────────────

function rowToListing(row: ServerListingRow): MarketplaceListing {
  const priceNumber = Number(row.price);
  // Phase 4.7 — Class-A fields (description, published_at) project
  // straight from the signed body. Class-B fields (rating, review_count,
  // installs) are server-maintained stats that default to zero on a
  // fresh deployment; the durable sidecar lands in Phase 4.5.
  // `contract` and `lineage` stay as light placeholders here — the
  // marketplace LIST endpoint doesn't carry them; the detail page
  // resolves them via `gi_root_id` once `getMarketplaceListing` is
  // wired against the engine (Q-6 follow-up).
  return {
    root_id: row.gi_root_id,
    version: row.gi_version,
    name: row.name,
    tagline: row.tags[0] ?? '',
    description: row.description ?? '',
    domain: parseDomain(row.domain),
    author_name: `key-${row.author_pubkey.slice(0, 8)}`,
    author_public_key: `ed25519:${row.author_pubkey}`,
    published_at: parsePublishedAt(row.published_at),
    price_usd: Number.isFinite(priceNumber) ? Math.max(0, Math.round(priceNumber)) : 0,
    rating: typeof row.rating === 'number' ? row.rating : 0,
    review_count: typeof row.review_count === 'number' ? row.review_count : 0,
    installs: typeof row.installs === 'number' ? row.installs : 0,
    certification: parseCertification(row.certification_level),
    contract: emptyContract(row.domain),
    lineage: emptyLineage(row.gi_root_id),
  };
}

function parsePublishedAt(raw: string | undefined): TrustedTimestamp {
  if (raw && raw.length > 0) {
    return { iso: raw, source: 'Monotonic' };
  }
  return nowTimestamp();
}

/** Server domain strings are Rust's Debug form. Named tags pass
 *  through; anything else (including `Conductor` and every `Custom`)
 *  is wrapped in `{Custom: …}` — the UI's `DomainType` uses
 *  `Custom('conductor')` for composite graphs. */
function parseDomain(raw: string): DomainType {
  switch (raw) {
    case 'Audio':
    case 'Process':
    case 'Medical':
    case 'Financial':
    case 'Defense':
    case 'Government':
    case 'Generic':
      return raw;
    default: {
      const match = /^Custom\("?([^")]+)"?\)$/.exec(raw);
      return { Custom: match?.[1] ?? raw };
    }
  }
}

function parseCertification(raw: string): CertificationLevel {
  switch (raw.toLowerCase()) {
    case 'gold':
      return 'gold';
    case 'silver':
      return 'silver';
    case 'bronze':
      return 'bronze';
    default:
      return 'none';
  }
}

function certificationToWire(c: CertificationLevel): string {
  switch (c) {
    case 'gold':
      return 'Gold';
    case 'silver':
      return 'Silver';
    case 'bronze':
      return 'Bronze';
    default:
      return 'None';
  }
}

function domainToWire(d: DomainType): string {
  if (typeof d === 'string') return d;
  return `Custom(${JSON.stringify(d.Custom)})`;
}

function emptyContract(domainDebug: string): MarketplaceListing['contract'] {
  return {
    inputs: [],
    outputs: [],
    parameters: [],
    behavior_rules: [],
    domain_adapter: parseDomain(domainDebug),
    macros: [],
  };
}

function emptyLineage(root_id: EntityId): Lineage {
  return { root_id, entries: [] };
}

function stripTaggedHex(tagged: string): string {
  const idx = tagged.indexOf(':');
  return idx === -1 ? tagged : tagged.slice(idx + 1);
}

/** Format a shekel price as the server's 12-digit fixed-point
 *  decimal string (`"10.000000000000"`). The server parses the
 *  fraction into a `FixedPoint12`. */
function twelveDigitFixedPoint(n: number): string {
  const whole = Math.max(0, Math.floor(n));
  return `${whole}.000000000000`;
}

function nowTimestamp(): TrustedTimestamp {
  return { iso: new Date().toISOString(), source: 'Monotonic' };
}

function canonicalHashHex(value: unknown): string {
  // `canonicalHash` returns `sha256:<hex>`; strip the tag so we
  // can slice it into the `lst_` id format the server expects.
  const tagged = canonicalHash(value);
  return stripTaggedHex(tagged);
}

// ─── Phase 17 — product detail, subscribe, creator profile ───────

export async function getProductBySlug(slug: string): Promise<ProductListing> {
  return apiFetch<ProductListing>(`/marketplace/products/${encodeURIComponent(slug)}`);
}

export async function subscribe(
  slug: string,
  req: SubscribeRequest,
): Promise<SubscribeResponse> {
  return apiFetch<SubscribeResponse>(
    `/marketplace/${encodeURIComponent(slug)}/subscribe`,
    { method: 'POST', body: req },
  );
}

export async function getCreatorProfile(pubkey: string): Promise<CreatorProfile> {
  return apiFetch<CreatorProfile>(`/creators/${encodeURIComponent(pubkey)}`);
}
